import Joi from 'joi';
import { useState } from 'react';
import { Form, useNavigate, redirect, useActionData, json, useLoaderData } from "react-router-dom";
import { fetchClient } from '../../util/fetchClient.js';
import RedExclamationCircle from '../_Shared/RedExclamationCircle.js';
import { COUNTRIES, STATES_MAP, CITIES_MAP } from '../_Shared/Addresses.js';

const fetchBranch = async id => {
  if (id) {
    const { body: branch } = await fetchClient(`/v1/branches/${id}`);
    return { branch };
  } else {
    return { branch: {} };
  }
};

export const loader = async ({ params }) => fetchBranch(params && params.id);

const getRequestBody = (data) => ({
  name: data.name,
  address_country: data.address_country,
  address_state: data.address_state,
  address_city: data.address_city,
  address_line1: data.address_line1,
  address_postal_code: data.address_postal_code,
  ...(data.id ? { id: data.id } : undefined)
});

const updateBranchSchema = Joi.object({
  id: Joi.string().required(),
  name: Joi.string().required(),
  address_country: Joi.string().required(),
  address_state: Joi.string().required(),
  address_city: Joi.string().required(),
  address_line1: Joi.string().required(),
  address_postal_code: Joi.number().integer().greater(0).required(),
}).options({ abortEarly: false });

const createBranchSchema = Joi.object({
  name: Joi.string().required(),
  address_country: Joi.string().required(),
  address_state: Joi.string().required(),
  address_city: Joi.string().required(),
  address_line1: Joi.string().required(),
  address_postal_code: Joi.number().integer().greater(0).required(),
}).options({ abortEarly: false });

export const action = async ({ request }) => {
  const data = Object.fromEntries(await request.formData());
  const body = getRequestBody(data);

  try {
    const branchSchema = request.method === 'POST' ? createBranchSchema : updateBranchSchema;
    await branchSchema.validateAsync(body, { abortEarly: false });
  } catch (validationError) {
    console.error(`validationError`, validationError);
    const errors = {};
    validationError?.details.map((details) => errors[details.context.key] = details.message);
    return json({ errors });
  }

  let response;
  switch (request.method) {
    case 'POST':
      response = await fetchClient('/v1/branches', { method: 'POST', body: JSON.stringify(body) });
      break;
    case 'PUT':
      response = await fetchClient(`/v1/branches/${body.id}`, { method: 'PUT', body: JSON.stringify(body) });
      break;
    default:
  }

  if (response?.response?.ok) {
    return redirect('/branches');
  } else {
    const message = `ERROR: Server failed to process the request(${response?.status})`;
    console.error(message);
    return json({ errors: { api: message } });
  }
};

export default function BranchesForm() {
  const navigate = useNavigate();
  const cancelButtonClick = () => navigate('/branches');
  const actionData = useActionData();
  const { branch } = useLoaderData();

  const [addressCountry, setAddressCountry] = useState(branch.address_country || COUNTRIES[0]);
  const [addressCountryStates, setAddressCountryStates] = useState(STATES_MAP[addressCountry]);
  const [addressState, setAddressState] = useState(branch.address_state || STATES_MAP[addressCountry][0]);
  const [addressStateCities, setAddressStateCities] = useState(CITIES_MAP[addressState]);
  const [addressCity, setAddressCity] = useState(branch.address_city || CITIES_MAP[addressState][0]);

  const handleChangeAddressCountry = (event) => {
    const newAddressCountry = event.target.value;
    setAddressCountry(newAddressCountry);
    setAddressCountryStates(STATES_MAP[newAddressCountry]);

    const newAddressState = STATES_MAP[newAddressCountry][0];
    setAddressState(newAddressState)
    setAddressStateCities(CITIES_MAP[newAddressState]);

    const newAddressCity = CITIES_MAP[newAddressState][0];
    setAddressCity(newAddressCity)
  };

  const handleChangeAddressState = (event) => {
    const newAddressState = event.target.value;
    setAddressState(newAddressState);
    setAddressStateCities(CITIES_MAP[newAddressState]);

    const newAddressCity = CITIES_MAP[newAddressState][0];
    setAddressCity(newAddressCity)
  };

  const handleChangeAddressCity = (event) => {
    const newAddressCity = event.target.value;
    setAddressCity(newAddressCity);
  };

  return (
    <Form method={branch.id ? "put" : "post"}>
      <input hidden id="id" name="id" defaultValue={branch.id} />

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="space-y-12">
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">Información de la Sucursal</h2>
            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label htmlFor="first-name" className="block text-sm font-medium leading-6 text-gray-900">
                  Nombre
                </label>
                <div className="mt-2">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="branch-name"
                    className={actionData?.errors?.name
                      ? "block w-full rounded-md border-0 py-1.5 text-rose-900 shadow-sm ring-1 ring-inset ring-rose-300 placeholder:text-rose-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6"
                      : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    }
                    defaultValue={branch.name}
                  />
                </div>
              </div>
            </div>

            <h2 className="text-base font-semibold leading-7 text-gray-900 pt-12">Domicilio de la Sucursal</h2>
            <div hidden>
              <label htmlFor="address_country" className="block text-sm font-medium leading-6 text-gray-900">
                País
              </label>
              <div className="mt-2">
                <select
                  id="address_country"
                  name="address_country"
                  autoComplete="address_country"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                  defaultValue={addressCountry}
                  onChange={handleChangeAddressCountry}
                >
                  {COUNTRIES.map(countryCode => {
                    return <option value={countryCode}>{countryCode}</option>
                  })}
                </select>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label htmlFor="address_state" className="block text-sm font-medium leading-6 text-gray-900">
                  Estado
                </label>
                <div className="mt-2">
                  <select
                    id="address_state"
                    name="address_state"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    defaultValue={addressState}
                    onChange={(e) => handleChangeAddressState(e)}
                  >
                    {addressCountryStates.map(state => {
                      return <option value={state}>{state}</option>
                    })}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="address_city" className="block text-sm font-medium leading-6 text-gray-900">
                  Ciudad
                </label>
                <div className="mt-2">
                  <select
                    id="address_city"
                    name="address_city"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    defaultValue={addressCity}
                    onChange={handleChangeAddressCity}
                  >
                    {addressStateCities.map(city => {
                      return <option value={city}>{city}</option>
                    })}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label htmlFor="line1" className="block text-sm font-medium leading-6 text-gray-900">
                  Domicilio
                </label>
                <div className="mt-2">
                  <input
                    id="address_line1"
                    name="address_line1"
                    type="text"
                    autoComplete="address_line1"
                    placeholder="Calle, Número, Colonia"
                    className={actionData?.errors?.address_line1
                      ? "block w-full rounded-md border-0 py-1.5 text-rose-900 shadow-sm ring-1 ring-inset ring-rose-300 placeholder:text-rose-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6"
                      : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    }
                    defaultValue={branch.address_line1}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="address_postal_code" className="block text-sm font-medium leading-6 text-gray-900">
                  Código postal
                </label>
                <div className="mt-2">
                <input
                    id="address_postal_code"
                    name="address_postal_code"
                    type="text"
                    autoComplete="address_postal_code"
                    className={actionData?.errors?.address_postal_code
                      ? "block w-full rounded-md border-0 py-1.5 text-rose-900 shadow-sm ring-1 ring-inset ring-rose-300 placeholder:text-rose-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6"
                      : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    }
                    defaultValue={branch.address_postal_code}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-x-6">
            <button type="button" onClick={cancelButtonClick} className="text-sm font-semibold leading-6 text-gray-900">
              Cancelar
            </button>
            <button
              type="submit"
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Guardar
            </button>
            {actionData?.errors && actionData.errors ? <RedExclamationCircle /> : null}
          </div>
        </div>
      </div>
    </Form>
  )
}
